export function getUniqueXpath(element: HTMLElement) {
  // Rank 1: data-pyne-id or other data attributes
  const dataAttributePath = getByDataAttribute(element);
  if (dataAttributePath.length > 0) {
    return { selector: dataAttributePath, rank: 1 };
  }

  // Rank 2: ID
  const idPath = getById(element);
  if (idPath.length > 0) {
    return { selector: idPath, rank: 2 };
  }

  // Rank 3: Specific attributes (aria, alt, placeholder, name)
  const attrPath = getByAttribute(element);
  if (attrPath.length > 0) {
    return { selector: attrPath, rank: 3 };
  }

  // Rank 4: Relative path from a stable anchor (NEW)
  const relativeAnchorPath = getByRelativeXPathFromAnchor(element);
  if (relativeAnchorPath.length > 0) {
    return { selector: relativeAnchorPath, rank: 4 };
  }

  // Rank 5: Unique Class
  const clsxPath = getByUniqueClass(element);
  if (clsxPath.length > 0) {
    return { selector: clsxPath, rank: 5 };
  }

  // Rank 6: Text content
  const textPath = getByText(element);
  if (textPath.length > 0) {
    return { selector: textPath, rank: 6 };
  }

  // Rank 7: Class combination
  const comboPath = getByClassCombo(element);
  if (comboPath.length > 0) {
    return { selector: comboPath, rank: 7 };
  }

  // Rank 8: Full XPath (fallback)
  const fullXPath = getFullXPath(element);
  if (fullXPath.length > 0) {
    return { selector: fullXPath, rank: 8 };
  }

  return { selector: "", rank: 100 }; // Default for no selector found
}

function getById(element: HTMLElement) {
  if (!element.id) return "";

  // Check if the ID contains any numbers
  if (/\d/.test(element.id)) {
    return "";
  }

  const xpath = `//*[@id="${element.id}"]`;
  if (isUnique(xpath, element)) {
    return xpath;
  }

  return "";
}
function getByDataAttribute(element: HTMLElement) {
  // Prioritize data-pyne-id
  const pyneId = element.getAttribute("data-pyne-id");
  if (pyneId && pyneId.length > 0) {
    const xpath = x(`[@data-pyne-id="${pyneId}"]`, element);
    if (xpath.length > 0) {
      return xpath;
    }
  }

  const attributes = element.attributes || [];
  for (let i = 0; i < attributes.length; i++) {
    const { name, value } = attributes[i];

    // Skip data-pyne-id as it's already handled
    if (name === "data-pyne-id") continue;

    if (name.startsWith("data-") && value.length > 0) {
      const xpath = x(`[@${name}="${value}"]`, element);
      if (xpath.length > 0) {
        return xpath;
      }
    }
  }
  return "";
}

function getByAttribute(element: HTMLElement) {
  const dataAttributePath = getByDataAttribute(element);
  if (dataAttributePath) {
    return dataAttributePath;
  }

  const attributes = element.attributes || [];
  for (let i = 0; i < attributes.length; i++) {
    const { name, value } = attributes[i];

    if (
      value.length === 0 ||
      name.startsWith("data-") || // skip data attributes as they are handled by getByDataAttribute
      (!name.startsWith("aria-") && name !== "alt" && name !== "placeholder" && name !== "name")
    )
      continue;

    const xpath = x(`[@${name}="${value}"]`, element);
    if (xpath.length > 0) {
      return xpath;
    }
  }

  return "";
}

function getByUniqueClass(element: HTMLElement) {
  if (!element.className) return "";

  const classList = Array.from(element.classList).filter(isValidClassName);

  for (let i = 0; i < classList.length; i++) {
    const xpath = x(`[contains(@class, "${classList[i]}")]`, element);
    if (xpath.length > 0) {
      return xpath;
    }
  }

  return "";
}

function getByText(element: HTMLElement) {
  if (!element.textContent) return "";

  const allowedTags = ["a", "button", "h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "li", "label"];
  const tagName = getTagName(element);

  if (allowedTags.includes(tagName)) {
    const textContent = element?.textContent
      ?.split("\n")
      .sort((a, b) => b.length - a.length)[0]
      .trim();

    if (textContent && textContent.length > 0) {
      const tagName = getTagName(element);
      const pathIndex = getElementIndexInParent(element);
      const xpathOptions = [
        `//*[contains(text(),"${textContent}")]`,
        `//*[text()="${textContent}"]`,
        `//*[contains(.,"${textContent}")]`,
        `//${tagName}[contains(text(),"${textContent}")]`,
        `//${tagName}[text()="${textContent}"]`,
        `//${tagName}[contains(.,"${textContent}")]`,
        `//${tagName}${pathIndex}[contains(text(),"${textContent}")]`,
        `//${tagName}${pathIndex}[text()="${textContent}"]`,
        `//${tagName}${pathIndex}[contains(.,"${textContent}")]`,
      ];

      const xpath = xpathOptions.find((xpath) => isUnique(xpath, element));
      return xpath || "";
    }
  }

  return "";
}

function getByClassCombo(element: HTMLElement) {
  if (!element.className) return "";

  const classList = Array.from(element.classList).filter(isValidClassName);
  const combinations = getClassCombinations(classList);
  for (const combo of combinations) {
    if (combo.length === 1) continue;
    const attr = combo.map((c) => `contains(@class,"${c}")`).join(" and ");
    const xpath = x(`[${attr}]`, element);
    if (xpath.length > 0) {
      return xpath;
    }
  }

  return "";
}

function getClassCombinations(classes: string[]) {
  const combinations = [];
  const n = classes.length;
  for (let i = 0; i < n; i++) {
    combinations.push([classes[i]]);
    for (let j = i + 1; j < n; j++) {
      combinations.push([classes[i], classes[j]]);
      for (let k = j + 1; k < n; k++) {
        combinations.push([classes[i], classes[j], classes[k]]);
      }
    }
  }
  return combinations;
}

function getFullXPath(element: HTMLElement) {
  if (element.nodeType !== Node.ELEMENT_NODE) return "";

  let path = "";

  while (element && element.nodeType === Node.ELEMENT_NODE) {
    let index = 1;
    let sibling = element.previousSibling;

    // Count how many siblings with the same tag name are before this element
    while (sibling) {
      if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === element.nodeName) {
        index++;
      }
      sibling = sibling.previousSibling;
    }

    const tagName = element.nodeName.toLowerCase();
    const step =
      index > 1 || (element.nextSibling && element.nextSibling.nodeName === element.nodeName)
        ? `${tagName}[${index}]`
        : tagName;

    path = `/${step}${path}`;
    element = element.parentNode as HTMLElement;
  }

  return path;
}

export function getTagName(element: HTMLElement) {
  const tagName = element.tagName.toLowerCase();
  return tagName;
}

export function isUnique(xpath: string, element: HTMLElement) {
  if (document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue === element) {
    return true;
  }

  return false;
}

export function x(attr: string, element: HTMLElement) {
  const tagName = getTagName(element);
  const pathIndex = getElementIndexInParent(element);

  let xpath = "//" + tagName + attr;
  if (isUnique(xpath, element)) {
    return xpath;
  }

  xpath = "//" + tagName + pathIndex + attr;
  if (isUnique(xpath, element)) {
    return xpath;
  }

  return "";
}

// New helper functions and strategy for anchor-based relative XPath
function getUniqueAnchorSelector(anchorElement: HTMLElement): string {
  // Check data attributes (including data-pyne-id)
  // getByDataAttribute already ensures uniqueness for the anchorElement itself.
  let selector = getByDataAttribute(anchorElement);
  if (selector) return selector;

  // Check ID
  // getById already ensures uniqueness for the anchorElement itself.
  selector = getById(anchorElement);
  if (selector) return selector;

  return "";
}

function getRelativeXPath(descendant: HTMLElement, ancestor: HTMLElement): string {
  if (descendant === ancestor) return "";

  let path = "";
  let currentElement: HTMLElement | Node | null = descendant;

  while (currentElement && currentElement !== ancestor && currentElement.nodeType === Node.ELEMENT_NODE) {
    const el = currentElement as HTMLElement;
    let index = 1;
    let sibling = el.previousSibling;

    while (sibling) {
      if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === el.nodeName) {
        index++;
      }
      sibling = sibling.previousSibling;
    }

    const tagName = el.nodeName.toLowerCase();
    const step =
      index > 1 || (el.nextSibling && el.nextSibling.nodeName === el.nodeName) ? `${tagName}[${index}]` : tagName;

    path = `/${step}${path}`;

    if (!el.parentNode) return "";
    currentElement = el.parentNode;
  }

  if (currentElement === ancestor) {
    return path;
  } else {
    return "";
  }
}

function getByRelativeXPathFromAnchor(element: HTMLElement): string {
  let currentParent = element.parentElement;
  const MAX_PARENT_SEARCH_DEPTH = 5;
  let depth = 0;

  while (currentParent && depth < MAX_PARENT_SEARCH_DEPTH) {
    const anchorSelector = getUniqueAnchorSelector(currentParent);

    if (anchorSelector) {
      const relativePath = getRelativeXPath(element, currentParent);
      if (relativePath) {
        const finalXPath = anchorSelector + relativePath;
        if (isUnique(finalXPath, element)) {
          return finalXPath;
        }
      }
    }

    currentParent = currentParent.parentElement;
    depth++;
  }

  return "";
}

// Build the path by traversing up the DOM tree
export function getElementIndexInParent(el: HTMLElement) {
  const siblings = Array.from(el.parentNode?.children || []).filter((e) => e.tagName === el.tagName);
  if (siblings.length > 1) {
    // Return 1-based index if there are siblings with the same tag
    return `[${siblings.indexOf(el) + 1}]`;
  }
  return ""; // No need for index if it's unique within its parent
}

export function isValidClassName(className: string) {
  // Reject class names that are too short
  if (className.length <= 2) return false;
  // Reject class names that contain digits
  if (/\d/.test(className)) return false;
  // Reject class names that start with known prefixes
  const rejectedPrefixes = ["css", "jsx", "tw", "tailwind", "Styled", "style"];
  for (const prefix of rejectedPrefixes) {
    if (className.startsWith(prefix)) return false;
  }
  // Reject common utility class names
  const rejectedClassNames = [
    "my",
    "mx",
    "mt",
    "mb",
    "ml",
    "mr",
    "p",
    "py",
    "px",
    "pt",
    "pb",
    "pl",
    "pr",
    "flex",
    "grid",
    "inline",
    "block",
    "hidden",
    "relative",
    "absolute",
    "fixed",
    "static",
    "sticky",
    "top",
    "bottom",
    "left",
    "right",
    "font",
    "leading",
    "tracking",
    "uppercase",
    "lowercase",
    "capitalize",
    "antialiased",
    "break",
    "whitespace",
    "truncate",
    "cursor",
    "pointer",
    "hover",
    "focus",
    "active",
    "visited",
    "disabled",
    "first",
    "last",
    "even",
    "odd",
    "col",
    "row",
    "gap",
    "space",
    "divide",
    "border",
    "rounded",
    "shadow",
    "opacity",
    "transition",
    "duration",
    "ease",
    "delay",
    "animate",
    "fill",
    "stroke",
    "transform",
    "scale",
    "rotate",
    "translate",
    "skew",
    "origin",
    "resize",
    "appearance",
    "outline",
    "table",
    "select",
    "svg",
    "filter",
    "blend",
    "isolation",
    "mix",
    "order",
    "placeholder",
    "ring",
    "float",
    "clear",
    "object",
    "overflow",
    "position",
    "z",
    "display",
    "text",
    "bg",
    "w",
    "h",
    "min",
    "max",
    "inset",
    "m",
    "p",
    "bg",
    "text",
    "border",
    "grow",
    "shrink",
    "justify",
    "items",
    "align",
  ];
  if (rejectedClassNames.some((cn) => cn === className || className.startsWith(cn + "-"))) return false;
  // Accept the className
  return true;
}
